import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RiskMatrixPanel = ({ risks }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Risks', icon: 'Shield' },
    { id: 'financial', label: 'Financial', icon: 'DollarSign' },
    { id: 'delivery', label: 'Delivery', icon: 'Truck' },
    { id: 'compliance', label: 'Compliance', icon: 'FileCheck' },
    { id: 'dependency', label: 'Dependency', icon: 'Link' }
  ];

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-error/10 text-error border-error',
      high: 'bg-warning/10 text-warning border-warning',
      medium: 'bg-accent/10 text-accent border-accent',
      low: 'bg-success/10 text-success border-success'
    };
    return colors?.[severity] || colors?.medium;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: 'AlertCircle',
      'in-progress': 'Clock',
      mitigated: 'CheckCircle2',
      closed: 'XCircle'
    };
    return icons?.[status] || 'AlertCircle';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-error',
      'in-progress': 'text-warning',
      mitigated: 'text-success',
      closed: 'text-muted-foreground'
    };
    return colors?.[status] || colors?.open;
  };

  const filteredRisks = selectedCategory === 'all' 
    ? risks 
    : risks?.filter(risk => risk?.category === selectedCategory);

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
      <div className="p-16 md:p-20 lg:p-24 border-b border-border">
        <div className="flex items-center justify-between mb-16">
          <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading">
            Risk Register
          </h3>
          <span className="px-12 py-6 bg-primary/10 text-primary rounded-md text-sm font-medium caption">
            {filteredRisks?.length} Active
          </span>
        </div>

        <div className="flex items-center gap-8 overflow-x-auto pb-4">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`
                flex items-center gap-8 px-12 py-8 rounded-md text-sm font-medium
                transition-smooth whitespace-nowrap flex-shrink-0
                ${selectedCategory === category?.id
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name={category?.icon} size={16} />
              {category?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-border max-h-96 md:max-h-[500px] overflow-y-auto">
        {filteredRisks?.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-24 text-center">
            <Icon name="ShieldCheck" size={48} className="text-success mb-12" />
            <p className="text-muted-foreground">No risks in this category</p>
          </div>
        ) : (
          filteredRisks?.map((risk) => (
            <div key={risk?.id} className="p-16 md:p-20 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start gap-12 mb-12">
                <div className={`
                  px-12 py-6 rounded-md text-xs font-semibold uppercase tracking-wider
                  border caption flex-shrink-0
                  ${getSeverityColor(risk?.severity)}
                `}>
                  {risk?.severity}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm md:text-base font-medium text-foreground mb-4">
                    {risk?.title}
                  </h4>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {risk?.description}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-12 md:gap-16">
                <div className="flex items-center gap-6">
                  <Icon name={getStatusIcon(risk?.status)} size={16} className={getStatusColor(risk?.status)} />
                  <span className="text-sm text-muted-foreground capitalize">
                    {risk?.status?.replace('-', ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{risk?.owner}</span>
                </div>
                <div className="flex items-center gap-6">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Review: {new Date(risk.reviewDate)?.toLocaleDateString()}
                  </span>
                </div>
              </div>

              {risk?.mitigation && (
                <div className="mt-12 p-12 bg-muted/50 rounded-md">
                  <p className="text-xs text-muted-foreground caption">
                    <span className="font-medium">Mitigation:</span> {risk?.mitigation}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RiskMatrixPanel;